import { Field, FieldProps, getIn } from 'formik';
import React, { Fragment, MouseEventHandler, useMemo } from "react";
import { Autocomplete, Chip, CircularProgress, FilterOptionsState, SxProps, TextField, Theme } from "@mui/material";
import { AutocompleteRenderOptionState } from "@mui/material/Autocomplete/Autocomplete";
import FilterWrapper from "./FilterWrapper";


const AutocompleteField = (props: AutocompleteFieldProps) => {
    return (
        <Field name={ props.name }>
            { ({ form: { errors, setFieldValue }, field }) => (
                <AutocompleteInput
                    { ...props }
                    value={ field.value }
                    error={ !!getIn(errors, field?.name || '-') }
                    helperText={ getIn(errors, field?.name || '-') || props.helperText }
                    onKeyUp={ props.onKeyUp }
                    onChange={ values => {
                        setFieldValue(props.name, values);
                        props.onChange && props.onChange(values)
                    } }
                />
            ) }
        </Field>
    );

}
export default AutocompleteField;

export const AutocompleteInput: React.FC<AutocompleteInputProps> = props => {
    const value = useMemo(
        () => {
            const value = props.sortValues ? props.sortValues(props.value) : props.value;
            if (value === undefined) {
                return props.multiple ? [] : null;
            }
            return value;
        },
        [props.value, props.sortValues]
    );

    return (
        <Autocomplete
            //@ts-ignore
            onPaste={ e => {
                if (props.onPasteTextWithMultipleValues) {
                    const value = e.clipboardData.getData('text/plain');

                    e.preventDefault();

                    const values = value.split(/[\n,;]+/)
                        .map(value => value.trim())
                        .filter(value => value.length > 0);
                    props.onPasteTextWithMultipleValues(values);
                }
            } }
            fullWidth
            filterSelectedOptions
            { ...props }
            isOptionEqualToValue={ (option1, option2) => props.allowDuplicates ? false : JSON.stringify(option1) === JSON.stringify(option2) }
            value={ value }
            onChange={ (e, values) => {
                props.onChange && props.onChange(values)
            } }
            id={ props.id }
            multiple={ props.multiple }
            sx={ { position: 'relative', ...props.sx } }
            freeSolo={ props.freeSolo }
            noOptionsText={ props.noOptionsText }
            disabled={ props.disabled || props.isLoading }
            filterOptions={ props.filterOptions }
            renderOption={ props.renderOption }
            renderTags={ (values, getTagProps) =>
                values.map((option: any, index: number) => (
                    <Fragment key={ props.allowDuplicates ? Math.random() : JSON.stringify(option) }>
                        <Chip
                            variant="outlined"
                            label={ props.getOptionLabel(option) } { ...getTagProps({ index }) }
                            size={ props.size }
                        />
                    </Fragment>
                ))
            }
            renderInput={ (params) => (
                <>
                    <TextField
                        type={ props.dropdownOnly ? 'hidden' : undefined }
                        { ...params }
                        label={ props.label }
                        placeholder={ props.placeholder }
                        error={ props.error }
                        helperText={ props.helperText }
                        size={ props.size }
                        required={ props.required }
                        autoComplete='off'
                        sx={ props.dropdownOnly ? {
                            zIndex: -1,
                            position: 'absolute'
                        } : undefined }
                    />
                    {
                        props.isLoading &&
                        <div style={ {
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            top: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            paddingRight: '10px'
                        } }>
                            <CircularProgress size={ 20 } sx={ { backgroundColor: 'white' } }/>
                        </div>
                    }
                </>
            ) }
        />
    );
}

export const AutocompleteFilter: React.FC<AutocompleteInputProps> = props => {
    const used =
        (!Array.isArray(props.value) && props.value !== undefined && props.value !== null)
        || (Array.isArray(props.value) && props.value.length !== 0)
        || (typeof props.value === 'string' && props.value.length !== 0);

    return (
        <FilterWrapper used={ used }>
            <AutocompleteInput { ...props } />
        </FilterWrapper>
    );
}

export type AutocompleteFieldProps<T = any> = AutocompleteInputProps<T> & {
    name: string
};

type AutocompleteInputProps<T = any> = {
    id?: any
    value?: any
    inputValue?: any
    multiple?: boolean,
    onChange?: (selectedOptions: Array<T> | T) => void
    onPasteTextWithMultipleValues?: (pastedOptions: Array<string>) => void
    options: Array<T>
    getOptionLabel: (option: T) => string
    disabled?: boolean
    label?: string,
    required?: boolean,
    placeholder?: string,
    type?: string,
    field?: Partial<FieldProps>,
    allowDuplicates?: boolean,
    error?: boolean,
    helperText?: string | React.ReactNode,
    size?: 'small' | 'medium' | undefined
    isLoading?: boolean,
    freeSolo?: boolean,
    noOptionsText?: string,
    filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
    onKeyUp?: (e: any) => void;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: T,
        state: AutocompleteRenderOptionState,
    ) => React.ReactNode;
    open?: boolean,
    dropdownOnly?: boolean,
    onClick?: MouseEventHandler<T> | undefined;
    filterSelectedOptions?: boolean;
    sx?: SxProps<Theme>;
    sortValues?: (values: T[]) => T[];
};